import React, { useCallback, useMemo, useState } from "react";
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMeCore } from "../../../../cores/me";
import { useRouterCore } from "../../../../cores/router";
import { boardApiService } from "../../../../cores/boards/services/api";
import { useGlobalCore } from "../../../../cores/globals";
import {
  BOARD_CARD_MODAL,
  CONTACT_COMPANY_EXPAND_EDIT_FORM,
  CONTACT_EXPAND_EDIT_FORM,
} from "../../../../constants/modal.constant";
import { useBoardCore } from "../../../../cores/boards";
import { useCompanyCore } from "../../../../cores/company";
import { useContactCore } from "../../../../cores/contacts";
import { useConfigCore } from "../../../../cores/config";
import { Position } from "../../../../cores/globals/interfaces/globalEntity";
import EditContactForm from "../../contacts/modal/EditContactForm";
import EditCompanyForm from "../../contactCompanies/modal/EditCompanyForm";
import "./CrmAiSearchModal.scss";

export type CrmAiOpportunityResult = {
  type: "opportunity";
  id: string;
  cardId?: number;
  title: string;
  description?: string;
  listId: string;
  boardId: string;
  boardName?: string;
};

export type CrmAiContactResult = {
  type: "contact";
  id: number;
  name?: string;
  email?: string;
  phoneNumber?: string;
};

export type CrmAiCompanyResult = {
  type: "company";
  id: number;
  name?: string;
  website?: string;
  city?: string;
  country?: string;
};

type AiSearchPayload = {
  interpretation?: {
    keywords?: string[];
    summary?: string;
    usedAi?: boolean;
  };
  results?: {
    opportunities?: CrmAiOpportunityResult[];
    contacts?: CrmAiContactResult[];
    companies?: CrmAiCompanyResult[];
  };
  totals?: {
    opportunities?: number;
    contacts?: number;
    companies?: number;
  };
};

interface Props {
  isOpen: boolean;
  boardId?: string;
  focus?: CrmAiSearchFocus;
  onClose: () => void;
}

export type CrmAiSearchFocus = "all" | "contacts" | "companies";

const personalizeSummary = (
  summary: string,
  query: string,
  focus: CrmAiSearchFocus
) => {
  let text = summary.trim();
  if (!text) {
    if (focus === "contacts") {
      return query
        ? `Buscás contactos relacionados con «${query.trim()}».`
        : "Buscás contactos en tu base.";
    }
    if (focus === "companies") {
      return query
        ? `Buscás empresas relacionadas con «${query.trim()}».`
        : "Buscás empresas en tu base.";
    }
    return query
      ? `Buscás información relacionada con «${query.trim()}».`
      : "Buscás en oportunidades, contactos y empresas.";
  }

  const replacements: [RegExp, string][] = [
    [/^El usuario busca/i, "Buscás"],
    [/^El usuario quiere/i, "Querés"],
    [/^El usuario necesita/i, "Necesitás"],
    [/^El usuario solicita/i, "Estás buscando"],
    [/^El usuario está buscando/i, "Estás buscando"],
    [/^Usuario busca/i, "Buscás"],
    [/^Se busca/i, "Buscás"],
  ];

  for (const [pattern, replacement] of replacements) {
    if (pattern.test(text)) {
      text = text.replace(pattern, replacement);
      break;
    }
  }

  return text;
};

export default function CrmAiSearchModal({
  isOpen,
  boardId,
  focus = "all",
  onClose,
}: Props) {
  const intl = useIntl();
  const { company } = useMeCore();
  const { goToOpportunityDetail } = useRouterCore();
  const { turnOffModal, turnOnModal } = useGlobalCore();
  const { getCard, getCustomFields, getLists, getBusinessBoards } = useBoardCore();
  const { getCompany } = useCompanyCore();
  const { getContact, getCompany: getContactCompany, getContactTags, getCustomFields: getContactCustomFields } =
    useContactCore();
  const { getCommentTypes } = useConfigCore();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<AiSearchPayload | null>(null);

  const reset = useCallback(() => {
    setQuery("");
    setError(null);
    setPayload(null);
    setLoading(false);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const onSearch = useCallback(async () => {
    if (!company?.id || !query.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const response = await boardApiService.aiSearch(
        company.id,
        query.trim(),
        boardId
      );
      setPayload(response?.payload || response);
    } catch (e: any) {
      const message =
        e?.response?.data?.message ||
        e?.message ||
        intl.formatMessage({ id: "app.crmBusiness.aiSearch.error" });
      setError(String(message));
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [boardId, company?.id, intl, query]);

  const openOpportunity = useCallback(
    async (item: CrmAiOpportunityResult) => {
      if (!company?.id) return;
      handleClose();
      await getCard(company.id, item.boardId, item.listId, item.id, true);
      getCustomFields(company.id, item.boardId);
      getLists(company.id, item.boardId);
      getCompany(company.id);
      turnOffModal(BOARD_CARD_MODAL);
      goToOpportunityDetail(item.boardId, item.listId, item.id);
    },
    [
      company?.id,
      getCard,
      getCustomFields,
      getLists,
      getCompany,
      goToOpportunityDetail,
      handleClose,
      turnOffModal,
    ]
  );

  const openContact = useCallback(
    async (item: CrmAiContactResult) => {
      if (!company?.id) return;
      handleClose();
      getContactTags(company.id);
      getCommentTypes(company.id);
      getContactCustomFields(company.id);
      getBusinessBoards(company.id);
      await getContact(company.id, String(item.id));
      turnOnModal(
        CONTACT_EXPAND_EDIT_FORM,
        <EditContactForm
          onDelete={() => turnOffModal(CONTACT_EXPAND_EDIT_FORM)}
          closeOnUpdating={false}
        />,
        Position.LEFT
      );
    },
    [
      company?.id,
      getBusinessBoards,
      getCommentTypes,
      getContact,
      getContactCustomFields,
      getContactTags,
      handleClose,
      turnOffModal,
      turnOnModal,
    ]
  );

  const openCompany = useCallback(
    async (item: CrmAiCompanyResult) => {
      if (!company?.id) return;
      handleClose();
      await getContactCompany(company.id, String(item.id));
      turnOnModal(
        CONTACT_COMPANY_EXPAND_EDIT_FORM,
        <EditCompanyForm
          onDelete={() => turnOffModal(CONTACT_COMPANY_EXPAND_EDIT_FORM)}
        />,
        Position.LEFT
      );
    },
    [company?.id, getContactCompany, handleClose, turnOffModal, turnOnModal]
  );

  const opportunities = payload?.results?.opportunities || [];
  const contacts = payload?.results?.contacts || [];
  const companies = payload?.results?.companies || [];
  const hasResults =
    opportunities.length + contacts.length + companies.length > 0;

  const interpretationSummary = useMemo(() => {
    if (!payload?.interpretation?.summary) return null;
    return personalizeSummary(payload.interpretation.summary, query, focus);
  }, [payload?.interpretation?.summary, query, focus]);

  const titleId =
    focus === "contacts"
      ? "app.crmBusiness.aiSearch.contacts.title"
      : focus === "companies"
        ? "app.crmBusiness.aiSearch.companies.title"
        : "app.crmBusiness.aiSearch.title";
  const hintId =
    focus === "contacts"
      ? "app.crmBusiness.aiSearch.contacts.hint"
      : focus === "companies"
        ? "app.crmBusiness.aiSearch.companies.hint"
        : "app.crmBusiness.aiSearch.hint";
  const placeholderId =
    focus === "contacts"
      ? "app.crmBusiness.aiSearch.contacts.placeholder"
      : focus === "companies"
        ? "app.crmBusiness.aiSearch.companies.placeholder"
        : "app.crmBusiness.aiSearch.placeholder";

  const contactsSection =
    contacts.length > 0 ? (
      <section className="crm-ai-search__section mt-3">
        <h6>
          {intl.formatMessage({ id: "app.crmBusiness.aiSearch.contacts" })}{" "}
          ({contacts.length})
        </h6>
        {contacts.map((item) => (
          <button
            key={item.id}
            type="button"
            className="crm-ai-search__result"
            onClick={() => openContact(item)}
          >
            <div className="font-weight-bold">
              {item.name || item.email || `#${item.id}`}
            </div>
            <div className="text-muted small">{item.email}</div>
          </button>
        ))}
      </section>
    ) : null;

  const companiesSection =
    companies.length > 0 ? (
      <section className="crm-ai-search__section mt-3">
        <h6>
          {intl.formatMessage({ id: "app.crmBusiness.aiSearch.companies" })}{" "}
          ({companies.length})
        </h6>
        {companies.map((item) => (
          <button
            key={item.id}
            type="button"
            className="crm-ai-search__result"
            onClick={() => openCompany(item)}
          >
            <div className="font-weight-bold">{item.name}</div>
            <div className="text-muted small">
              {[item.city, item.country, item.website]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </button>
        ))}
      </section>
    ) : null;

  const opportunitiesSection =
    opportunities.length > 0 ? (
      <section className="crm-ai-search__section mt-3">
        <h6>
          {intl.formatMessage({
            id: "app.crmBusiness.aiSearch.opportunities",
          })}{" "}
          ({opportunities.length})
        </h6>
        {opportunities.map((item) => (
          <button
            key={item.id}
            type="button"
            className="crm-ai-search__result"
            onClick={() => openOpportunity(item)}
          >
            <div className="font-weight-bold">{item.title}</div>
            <div className="text-muted small">
              #{item.cardId}
              {item.boardName ? ` · ${item.boardName}` : ""}
            </div>
          </button>
        ))}
      </section>
    ) : null;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg" zIndex={2100}>
      <ModalHeader toggle={handleClose}>
        <FontAwesomeIcon icon={["fas", "magic"]} className="mr-2 text-primary" />
        {intl.formatMessage({ id: titleId })}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted mb-3" style={{ fontSize: 14 }}>
          {intl.formatMessage({ id: hintId })}
        </p>
        <div className="crm-ai-search__input-row">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={intl.formatMessage({ id: placeholderId })}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onSearch();
              }
            }}
          />
          <Button
            color="primary"
            className="ml-2"
            disabled={loading || !query.trim()}
            onClick={onSearch}
          >
            {loading ? (
              <Spinner size="sm" />
            ) : (
              intl.formatMessage({ id: "app.crmBusiness.aiSearch.search" })
            )}
          </Button>
        </div>

        {interpretationSummary && (
          <div className="crm-ai-search__interpretation mt-3">
            <strong>
              {intl.formatMessage({ id: "app.crmBusiness.aiSearch.understood" })}
              :
            </strong>{" "}
            {interpretationSummary}
            {!!payload?.interpretation?.keywords?.length && (
              <span className="crm-ai-search__keywords text-muted">
                {" "}
                (
                {(payload.interpretation.keywords || []).join(", ")})
              </span>
            )}
          </div>
        )}

        {error && <div className="text-danger mt-3">{error}</div>}

        {payload && !hasResults && !loading && (
          <div className="crm-ai-search__empty mt-3">
            {intl.formatMessage({ id: "app.crmBusiness.aiSearch.empty" })}
          </div>
        )}

        {focus === "contacts" && (
          <>
            {contactsSection}
            {companiesSection}
            {opportunitiesSection}
          </>
        )}
        {focus === "companies" && (
          <>
            {companiesSection}
            {contactsSection}
            {opportunitiesSection}
          </>
        )}
        {focus === "all" && (
          <>
            {opportunitiesSection}
            {contactsSection}
            {companiesSection}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={handleClose}>
          {intl.formatMessage({ id: "app.crmBusiness.aiSearch.close" })}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
